import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteOptionGroup, listOptionGroups, updateOptionGroup } from '../../api/business/endpoints';
import type { OptionGroup } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import { CompanySelector } from '../../components/CompanySelector';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { missingLanguages, textsOf } from '../../components/localisedText';
import { MissingLanguagesChip } from '../../components/MissingLanguagesChip';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { OptionGroupDialog } from './OptionGroupDialog';
import { OptionValuesDialog } from './OptionValuesDialog';

type GroupSortKey = 'position' | 'name' | 'values';

const VALUE_OF: Record<GroupSortKey, (group: OptionGroup) => string | number | null> = {
  position: (group) => group.position,
  name: (group) => group.name,
  values: (group) => group.values.length,
};

type Dialog =
  | { kind: 'group'; group: OptionGroup | null }
  | { kind: 'values'; groupId: string }
  | { kind: 'delete'; group: OptionGroup };

/**
 * The company's option groups — size, colour, pack — and their values, which its products vary by (ADR-0010).
 * The whole list is held, so it sorts in the browser (Conventions/08 §3).
 */
export const OptionGroupsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('products.read');
  const company = selection.company;
  const [term, setTerm] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const sort = useSort<GroupSortKey>('position');
  const { languages } = useContentLanguages();
  const canManage =
    company !== null && permissions.writable && permissions.can('products.manage', company.id);

  const query = useQuery({
    queryKey: ['option-groups', company?.id],
    enabled: company !== null,
    queryFn: () => listOptionGroups(company?.id ?? ''),
  });
  const move = useAppMutation(
    ({ groupId, position }: { groupId: string; position: number }) =>
      updateOptionGroup(company?.id ?? '', groupId, { position }),
    ['option-groups', 'product'],
  );
  const remove = useAppMutation(
    (groupId: string) => deleteOptionGroup(company?.id ?? '', groupId),
    ['option-groups', 'product'],
    () => setDialog(null),
  );

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('optionGroups.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('products.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  const groups = query.data ?? [];
  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    groups.filter(
      (group) =>
        needle === '' ||
        group.name.toLocaleLowerCase('sk').includes(needle) ||
        group.values.some((value) => value.value.toLocaleLowerCase('sk').includes(needle)),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );
  const valuesOf =
    dialog?.kind === 'values' ? groups.find((group) => group.id === dialog.groupId) : undefined;

  return (
    <>
      <PageHeader
        title={t('optionGroups.title')}
        subtitle={t('optionGroups.subtitle')}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CompanySelector
              companies={selection.companies}
              value={company}
              onChange={(companyId) => selection.select(companyId)}
            />
            {canManage ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialog({ kind: 'group', group: null })}
              >
                {t('optionGroups.create')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice
        error={query.error ?? selection.error ?? move.error}
        onRetry={() => void query.refetch()}
      />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('optionGroups.searchHint')}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="position">
                  {t('optionGroups.position')}
                </SortableCell>
                <SortableCell sort={sort} column="name">
                  {t('optionGroups.name')}
                </SortableCell>
                <SortableCell sort={sort} column="values">
                  {t('optionGroups.values')}
                </SortableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading || selection.isLoading ? (
                <TableSkeletonRows columns={4} />
              ) : (
                rows.map((group) => (
                  <TableRow key={group.id} hover>
                    <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>
                      {group.position + 1}
                      {canManage ? (
                        <>
                          <Tooltip title={t('common.moveUp')}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={move.isPending || group.position === 0}
                                onClick={() =>
                                  move.mutate({ groupId: group.id, position: group.position - 1 })
                                }
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('common.moveDown')}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={move.isPending || group.position === groups.length - 1}
                                onClick={() =>
                                  move.mutate({ groupId: group.id, position: group.position + 1 })
                                }
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 560 }}>
                      {group.name}
                      <MissingLanguagesChip
                        missing={missingLanguages(textsOf(group.translations, 'name', languages), languages)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {group.values.length === 0
                          ? t('optionGroups.noValues')
                          : group.values.map((value) => (
                              <Chip key={value.id} size="small" variant="outlined" label={value.value} />
                            ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button size="small" onClick={() => setDialog({ kind: 'values', groupId: group.id })}>
                          {t('optionGroups.editValues')}
                        </Button>
                        {canManage ? (
                          <>
                            <Button size="small" onClick={() => setDialog({ kind: 'group', group })}>
                              {t('optionGroups.rename')}
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              onClick={() => setDialog({ kind: 'delete', group })}
                            >
                              {t('common.delete')}
                            </Button>
                          </>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && company !== null && rows.length === 0 ? (
          <EmptyState
            title={t('optionGroups.empty')}
            detail={canManage ? t('optionGroups.emptyManage') : t('optionGroups.emptyRead')}
            action={
              canManage ? (
                <Button startIcon={<AddIcon />} onClick={() => setDialog({ kind: 'group', group: null })}>
                  {t('optionGroups.create')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </Paper>

      {dialog?.kind === 'group' && company ? (
        <OptionGroupDialog companyId={company.id} group={dialog.group} onClose={() => setDialog(null)} />
      ) : null}
      {valuesOf && company ? (
        <OptionValuesDialog
          companyId={company.id}
          group={valuesOf}
          canManage={canManage}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'delete' ? (
        <ConfirmDialog
          title={t('optionGroups.deleteTitle', { name: dialog.group.name })}
          message={t('optionGroups.deleteMessage')}
          confirmLabel={t('common.delete')}
          onConfirm={() => remove.mutate(dialog.group.id)}
          onClose={() => setDialog(null)}
          isBusy={remove.isPending}
          error={remove.error}
        />
      ) : null}
    </>
  );
};
